/**
 * MidLockup — o logótipo miD onde a mascote É o «i».
 *
 * Segue `mid-manual-mascote-lockup`: o «m» e o «D» mantêm-se em Poppins 600
 * e a mascote ocupa EXACTAMENTE o lugar do «i». Lê-se «miD» antes de se ler
 * personagem.
 *
 * A arte da mascote é IDÊNTICA ao estado "idle" de MidMascot.jsx (mesmo olho,
 * mesmo anel de lente, mesmos brilhos e o mesmo piscar que cresce a altura
 * 0→60 e volta — em repouso a pálpebra não existe). Aqui só está recortada
 * (viewBox 33 10 84 162) para o alinhamento tipográfico do logótipo.
 *
 * Construção (do manual):
 *   • Altura do corpo = altura de maiúscula · Base = linha de base
 *   • Largura da mascote = 0,50 em · Folga lateral = 0,045 em
 *   • m e D em Poppins 600, espacejamento -0,05 em
 *
 * tone (só Zonamento): "night" por omissão (fundos escuros), "auto" segue o âmbito dia/noite.
 *
 * Variantes:
 *   • vivo   — com lente (≥ 48px): ecrã inicial, onboarding, compradores.
 *   • neutro — sem lente, o «i» volta a ser letra (< 48px): cabeçalhos/favicon.
 *   • fiel   — m roxo, D prateado (cores do logótipo original): institucional.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { NEW_BRAND } from '../lib/brand';
import { BrandLogo } from './brand/BrandLogo';

const TEAL = '#3CC8B5';
const TEAL_TINT = '#7FE3D3';
const TEAL_DEEP = '#2AA492';
const LENS = '#0E2C2A';
const IRIS = '#A78BD9';
const PUPIL = '#1A1B2E';
const PURPLE = '#A78BD9';
const SILVER = '#C7C9D9';
const EASE = [0.42, 0, 0.58, 1];

let _uid = 0;

export default function MidLockup({
  variant = 'vivo',
  fontSize = 40,
  color = '#FAFAF7',
  dColor,
  animated = true,
  className = '',
  onClick,
  'data-testid': testId = 'mid-lockup',
  // Every caller sits on a dark ground (hence the light default colour), so night by default.
  tone = 'night',
}) {
  const isFiel = variant === 'fiel';
  const isNeutro = variant === 'neutro';
  const mColor = isFiel ? PURPLE : color;
  const dCol = dColor || (isFiel ? SILVER : color);
  const shineId = React.useMemo(() => `mid-lockup-shine-${++_uid}`, []);
  const isClickable = typeof onClick === 'function';

  // Zonamento: the outlined lockup, cap height close to the old m/D (mid-v6k8.8).
  if (NEW_BRAND) {
    return (
      <span
        className={className}
        onClick={onClick}
        data-testid={testId}
        data-variant={variant}
        role="img"
        aria-label="miD, Mediador Imobiliário Digital"
        style={{ display: 'inline-flex', cursor: isClickable ? 'pointer' : undefined }}
      >
        <BrandLogo height={Math.round(fontSize * 1.2)} tone={tone} label={null} />
      </span>
    );
  }

  const float = animated
    ? { animate: { y: [0, -4.75, 0] }, transition: { duration: 3, ease: 'easeInOut', repeat: Infinity } }
    : {};

  return (
    <span
      className={className}
      onClick={onClick}
      data-testid={testId}
      data-variant={variant}
      role="img"
      aria-label="miD, Mediador Imobiliário Digital"
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        fontFamily: "'Poppins', system-ui, sans-serif",
        fontWeight: 600,
        fontSize: `${fontSize}px`,
        lineHeight: 1,
        letterSpacing: '-0.05em',
        color: mColor,
        userSelect: 'none',
        cursor: isClickable ? 'pointer' : undefined,
      }}
    >
      <span aria-hidden="true">m</span>
      <svg
        aria-hidden="true"
        width="0.5em"
        height="0.964em"
        viewBox="33 10 84 162"
        style={{ margin: '0 0.045em', display: 'inline-block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={shineId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
            <stop offset="60%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Pingo / "cabeça" — flutua ~4,75u em 3s */}
        <motion.g {...float}>
          <circle cx="75" cy="26" r="16" fill={TEAL} />
          <circle cx="70" cy="21" r="5" fill={TEAL_TINT} opacity="0.85" />
        </motion.g>

        {isNeutro ? (
          /* Lockup neutro: o «i» volta a ser letra (sem lente). */
          <rect x="47" y="54" width="56" height="118" rx="8" fill={TEAL} />
        ) : (
          <>
            {/* Corpo (haste do i) — nunca se mexe */}
            <rect x="35" y="54" width="80" height="118" rx="34" fill={TEAL} />
            <rect x="35" y="54" width="80" height="118" rx="34" fill={`url(#${shineId})`} />

            {/* Lente + anel */}
            <circle cx="75" cy="104" r="30" fill={LENS} />
            <circle cx="75" cy="104" r="30" fill="none" stroke={TEAL_DEEP} strokeWidth="3" />

            {/* Íris + pupila + brilhos (repouso) */}
            <circle cx="75" cy="104" r="16" fill={IRIS} />
            <circle cx="75" cy="104" r="7" fill={PUPIL} />
            <circle cx="68" cy="97" r="5" fill="#fff" opacity="0.9" />
            <circle cx="83" cy="112" r="2.6" fill="#fff" opacity="0.5" />

            {/* Pálpebra — piscar 0,18s a cada 5,5s (cresce a altura e volta).
                Em repouso a altura é 0, por isso a lente fica limpa. */}
            {animated && (
              <motion.rect
                x="45"
                width="60"
                rx="8"
                fill={TEAL}
                initial={{ y: 74, height: 0 }}
                animate={{ y: [74, 74, 74, 74], height: [0, 0, 60, 0] }}
                transition={{ duration: 5.5, times: [0, 0.955, 0.972, 1], ease: EASE, repeat: Infinity }}
              />
            )}
          </>
        )}
      </svg>
      <span aria-hidden="true" style={{ color: dCol }}>D</span>
    </span>
  );
}

export { MidLockup };
